import { useDrag } from '@use-gesture/react';
import clsx from 'clsx';
import { useImperativeHandle, useRef } from 'react';

import { SELECTED_AREA_RADIUS } from '../overlays/constants.js';
import { useSelectionAreaPathRef } from './hooks/use-selection-area-path-ref.js';
import { selectionArea } from './SelectionArea.sty.js';

interface SelectionBackdropProps {
  focusable: boolean;
  startX: number;
  endX: number;
  height: number;
  isSynchronizedSelection: boolean;
  interactable: boolean;
  onDragMove?: (x: number) => void;
  hasTopLabels?: boolean;
  hasBottomLabels?: boolean;
}

const createSelectionAreaOutline = (
  startX: number,
  endX: number,
  height: number,
  radius: number,
  hasRoundedTop: boolean,
  hasRoundedBottom: boolean,
): string => {
  const left = Math.min(startX, endX);
  const right = Math.max(startX, endX);
  const selectionWidth = right - left;
  const minimumSelectionSize = 2 * radius * 0.75;
  const canvasPixelCorrection = 0.5;
  const calculatedRadius =
    selectionWidth > minimumSelectionSize
      ? radius
      : Math.floor(selectionWidth * 0.75);

  const r = Math.min(calculatedRadius, (right - left) / 2, height / 2);

  let d = '';
  if (hasRoundedTop) {
    d += `M${left + r},${canvasPixelCorrection}`;
    d += `H${right - r}`;
    d += `A${r},${r} 0 0 1 ${right},${canvasPixelCorrection + r}`;
  } else {
    d += `M${left},${canvasPixelCorrection}`;
    d += `H${right}`;
  }

  if (hasRoundedBottom) {
    d += `V${height - r}`;
    d += `A${r},${r} 0 0 1 ${right - r},${height}`;
    d += `H${left + r}`;
    d += `A${r},${r} 0 0 1 ${left},${height - r}`;
  } else {
    d += `V${height}`;
    d += `H${left}`;
  }

  if (hasRoundedTop) {
    d += `V${canvasPixelCorrection + r}`;
    d += `A${r},${r} 0 0 1 ${left + r},${canvasPixelCorrection}`;
  } else {
    d += `V${canvasPixelCorrection}`;
  }

  d += 'Z';
  return d;
};

export const SelectionBackdrop = ({
  startX,
  endX,
  height,
  focusable,
  interactable,
  onDragMove,
  isSynchronizedSelection,
  hasTopLabels = false,
  hasBottomLabels = false,
}: SelectionBackdropProps) => {
  const selectionAreaRef = useRef<SVGPathElement>(null);
  const forwardedRef = useSelectionAreaPathRef();
  useImperativeHandle(
    forwardedRef,
    () => ({
      element: selectionAreaRef.current,
      focus() {
        selectionAreaRef.current?.focus();
      },
    }),
    [],
  );

  const dragHandlers = useDrag(
    ({ type, delta }: { type: string; delta: number[] }) => {
      if (!(type === 'pointerdown' || type === 'pointerup')) {
        onDragMove?.(delta[0]);
      }
    },
    {},
  );

  return (
    <path
      ref={selectionAreaRef}
      tabIndex={focusable ? 0 : -1}
      strokeDasharray={isSynchronizedSelection ? '3,2' : '0,0'}
      className={clsx(selectionArea)}
      pointerEvents={interactable ? 'all' : 'none'}
      d={createSelectionAreaOutline(
        startX,
        endX,
        height,
        SELECTED_AREA_RADIUS,
        !hasTopLabels,
        !hasBottomLabels,
      )}
      {...dragHandlers()}
    />
  );
};
